/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.pages

import io.peekandpoke.klang.ui.comp.MarkdownDisplay
import io.peekandpoke.kraft.components.NoProps
import io.peekandpoke.kraft.components.PureComponent
import io.peekandpoke.kraft.components.comp
import io.peekandpoke.kraft.vdom.VDom
import io.peekandpoke.ultra.html.key
import io.peekandpoke.ultra.semanticui.noui
import io.peekandpoke.ultra.semanticui.ui
import kotlinx.html.FlowContent
import kotlinx.html.Tag
import kotlinx.html.a

@Suppress("FunctionName")
fun Tag.CreditsPage() = comp {
    CreditsPage(it)
}

class CreditsPage(ctx: NoProps) : PureComponent(ctx) {

    //  STATE  //////////////////////////////////////////////////////////////////////////////////////////////////

    //  IMPL  ///////////////////////////////////////////////////////////////////////////////////////////////////

    override fun VDom.render() {
        ui.fluid.container {
            key = "credits-page"

            ui.basic.segment {

                ui.header H1 { +"Credits" }

                ui.two.column.stackable.doubling.cards {
                    renderStrudelCard()
                    renderTidalCard()
                    renderMusicCodersCard()
                    renderFiltersCard()
                    renderDspAlgorithmsCard()
                    renderHardwareCard()
                    renderAcousticsCard()
                    renderMusicAndAudioCard()
                    renderSamplesCard()
                    renderBrowserCard()
                    renderGraphicsCard()
                    renderEditorCard()
                    renderFoundationCard()
                    renderUiCard()
                    renderKraftUltraCard()
                    renderRuntimeCard()
                    renderTestingCard()
                    renderAiCard()
                }
            }
        }
    }

    private fun FlowContent.renderStrudelCard() {
        noui.card {
            noui.content {
                ui.header H2 { +"Shout out to Strudel" }

                ui.divider()

                MarkdownDisplay(
                    """
                        **Klang exists because of Strudel.** What started as pure curiosity — *"how does this work?"* —
                        turned into a deep dive into the world of audio engines, pattern languages, and music synthesis.

                        Strudel's open-source code, its open sample libraries, and the excellent documentation at
                        [strudel.cc](https://strudel.cc) were invaluable in understanding the machinery behind it all.

                        What started as a copycat of Strudel is now evolving into its sibling — **Sprudel**.
                        Sharing the same roots and many of the same ideas, Sprudel takes the pattern language
                        in new directions as part of Klang.

                        And Strudel itself descends from **Tidal Cycles**, the project that started it all — see below.

                        A heartfelt thank you to the Strudel community for making all of this openly available.
                    """.trimIndent()
                )

                ui.divider()

                ui.list {
                    noui.item {
                        a(href = "https://strudel.cc", target = "_blank") { +"https://strudel.cc" }
                    }
                    noui.item {
                        a(href = "https://codeberg.org/uzu/strudel", target = "_blank") { +"Strudel on Codeberg" }
                    }
                }
            }
        }
    }

    private fun FlowContent.renderTidalCard() {
        noui.card {
            noui.content {
                ui.header H2 { +"Tidal Cycles — The Roots" }

                ui.divider()

                MarkdownDisplay(
                    """
                        **Before Strudel, there was Tidal Cycles.** Strudel is a JavaScript reimagining of
                        **[Tidal Cycles](https://tidalcycles.org)** — the Haskell live coding environment created
                        by **Alex McLean** that pioneered the cyclic, pattern-based language at the heart of all
                        of this. The way patterns describe music in Strudel, and now in Sprudel, traces directly
                        back to Tidal.

                        Lineage: **Tidal Cycles → Strudel → Sprudel**.
                    """.trimIndent()
                )

                ui.divider()

                ui.list {
                    noui.item {
                        a(href = "https://tidalcycles.org", target = "_blank") { +"https://tidalcycles.org" }
                    }
                    noui.item {
                        a(href = "https://github.com/tidalcycles/Tidal", target = "_blank") { +"Tidal on GitHub" }
                    }
                }
            }
        }
    }

    private fun FlowContent.renderMusicCodersCard() {
        noui.card {
            noui.content {
                ui.header H2 { +"Music Coders" }

                ui.divider()

                MarkdownDisplay(
                    """
                        The people who make live coding visible — and got this whole journey started:

                        - **[Switch Angel](https://www.youtube.com/@Switch-Angel)** — it was a YouTube short
                        of a Strudel live coding session that first sparked the curiosity behind Klang.
                        Without that video, this project would not exist. On top of that, the majority of
                        the samples in the default drum kit ([uzu-drumkit](https://github.com/tidalcycles/uzu-drumkit))
                        were contributed by her — the kicks, snares, and hats you hear first are her sounds.
                    """.trimIndent()
                )

                ui.divider()

                ui.list {
                    noui.item {
                        a(href = "https://www.youtube.com/@Switch-Angel", target = "_blank") { +"Switch Angel on YouTube" }
                    }
                }
            }
        }
    }

    private fun FlowContent.renderBrowserCard() {
        noui.card {
            noui.content {
                ui.header H2 { +"The Modern Browser" }

                ui.divider()

                MarkdownDisplay(
                    """
                        None of this would be possible without the incredible platform that modern browsers have become.
                        The performance and the richness of APIs available today are nothing short of amazing:

                        - **[Web Audio API](https://developer.mozilla.org/en-US/docs/Web/API/Web_Audio_API)** — real-time audio synthesis and processing
                        - **[AudioWorklet](https://developer.mozilla.org/en-US/docs/Web/API/AudioWorklet)** — high-performance audio processing on a dedicated thread
                        - **[Canvas API](https://developer.mozilla.org/en-US/docs/Web/API/Canvas_API)** — rendering oscilloscopes, waveforms, and visualizations
                        - **[Web Workers](https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API)** — parallel processing without blocking the UI
                        - **[The structured clone algorithm](https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Structured_clone_algorithm)**
                        (the HTML standard behind `postMessage`): it copies an object graph with its shared references intact,
                        which is how a sound used twice in a song reaches the audio thread as one sound (`@WireShared`)
                    """.trimIndent()
                )
            }
        }
    }

    private fun FlowContent.renderFoundationCard() {
        noui.card {
            noui.content {
                ui.header H2 { +"The Foundation" }

                ui.divider()

                MarkdownDisplay(
                    """
                        Klang is built on the shoulders of these giants:

                        - **[Kotlin](https://kotlinlang.org)** & **[Kotlin Multiplatform](https://kotlinlang.org/docs/multiplatform.html)** —
                        the language and multiplatform foundation that makes it possible to share code across platforms
                        - **[KotlinX Coroutines](https://github.com/Kotlin/kotlinx.coroutines)** — async programming made elegant
                        - **[KotlinX Serialization](https://github.com/Kotlin/kotlinx.serialization)** — data serialization
                        - **[Ktor](https://ktor.io)** — HTTP framework powering the backend
                        - **[Gradle](https://gradle.org)** — build automation
                        - **[Google KSP](https://github.com/google/ksp)** — Kotlin Symbol Processing for code generation
                        - **[Clikt](https://ajalt.github.io/clikt/)** — command-line interface framework for the CLI tools
                    """.trimIndent()
                )
            }
        }
    }

    private fun FlowContent.renderEditorCard() {
        noui.card {
            noui.content {
                ui.header H2 { +"The Editor" }

                ui.divider()

                MarkdownDisplay(
                    """
                        The live coding experience is powered by:

                        - **[CodeMirror 6](https://codemirror.net)** — the code editor at the heart of the coding experience
                        - **[Lezer](https://lezer.codemirror.net)** — parser generator powering syntax highlighting
                        - **[codemirror-minimap](https://github.com/replit/codemirror-minimap)** by Replit: the small overview of the whole song beside the code editor
                        - **Ideas from the code editors and IDEs we have worked in for years**: numbered bookmarks, the jump back to
                          where you last edited, and Ctrl+click to a definition. Habits every programmer's editor taught us, and we wanted
                          them in the song editor too
                    """.trimIndent()
                )
            }
        }
    }

    private fun FlowContent.renderAcousticsCard() {
        noui.card {
            noui.content {
                ui.header H2 { +"Acoustics & Physical Modeling" }

                ui.divider()

                MarkdownDisplay(
                    """
                        Klang's body-resonator materials are *caricatures* — a few resonant modes that let the
                        ear recognize an instrument body, not measured replicas. The load-bearing landmark
                        frequencies are anchored from the acoustics literature:

                        - **Neville H. Fletcher & Thomas D. Rossing — *The Physics of Musical Instruments*** — the
                        resonance landmarks behind the body materials: guitar/string air + top-plate modes (the
                        wood tonewoods), the violin corpus + "bridge hill" (`violin`), the inharmonic bell partials
                        including the minor-third tierce (`bell`), and the brass formants (`brass`)
                        - **Arthur H. Benade — *Fundamentals of Musical Acoustics*** — brass and air-column
                        acoustics informing the `brass` formant regions
                        - **Johan Sundberg — *The Science of the Singing Voice*** — the "singer's formant"
                        (the ~2.8–3.4 kHz vocal "ring") behind the `croon` material

                        Everything beyond those landmarks is sparse plausible fill, tuned by ear.
                    """.trimIndent()
                )
            }
        }
    }

    private fun FlowContent.renderMusicAndAudioCard() {
        noui.card {
            noui.content {
                ui.header H2 { +"Music & Audio" }

                ui.divider()

                MarkdownDisplay(
                    """
                        Making music possible:

                        - **[Tonal.js](https://github.com/tonaljs/tonal)** — music theory library (ported to Kotlin in the :tones module)
                        - **[SoundFont2](https://www.npmjs.com/package/soundfont2)** — SoundFont parsing
                        - **[JLayer](http://www.javazoom.net/javalayer/javalayer.html)** — MP3 decoding on the JVM
                    """.trimIndent()
                )
            }
        }
    }

    private fun FlowContent.renderFiltersCard() {
        noui.card {
            noui.content {
                ui.header H2 { +"Filters & DSP" }

                ui.divider()

                MarkdownDisplay(
                    """
                        Klang's filters stand on the shoulders of the virtual-analog filter community:

                        - **"The Art of VA Filter Design" by Vadim Zavalishin** — the zero-delay-feedback (TPT)
                        state-variable filter that powers Klang's lowpass, highpass, bandpass, and notch filters
                        - **[Cytomic](https://cytomic.com/technical-papers/) (Andrew Simper)** — the canonical
                        trapezoidal-integrator SVF formulation that Klang's implementation follows
                        - **[Obxd](https://github.com/2DaT/Obxd) by Vadim Filatov (2DaT)** — inspiration for the
                        analog-style "diode-pair" resonance saturation that gives the filters their warmth when
                        driven (`analog > 0`). Klang's SVF topology and the way the nonlinearity is folded in are
                        its own; the *idea* of steering resonance damping from a diode-pair model comes from here.
                        - **Linkwitz-Riley crossovers** (Siegfried Linkwitz and Russ Riley, *"Active Crossover Networks for
                        Noncoincident Drivers"*, JAES 1976): the crossover of `bands`. Two Butterworth filters in series on
                        each side of a cut, so the bands sum back to flat level; with three bands or more each band below a
                        cut also passes that cut's all-pass, so the bands stay in phase with each other
                        - **Polyphase IIR half-band filters** (R. A. Valenzuela and A. G. Constantinides, *"Digital signal
                        processing schemes for efficient interpolation and decimation"*, IEE Proceedings, 1983; Artur Krukowski
                        and Izzet Kale, *"The design of arbitrary-band multi-path polyphase IIR filters"*, ISCAS 2001): the
                        oversampler. A half-band built from two chains of all-pass sections, flat in level and steep, at a few
                        samples of latency. Its coefficients follow the closed form of Laurent de Soras' free
                        [HIIR](http://ldesoras.free.fr/prod.html) library (WTFPL)
                    """.trimIndent()
                )
            }
        }
    }

    private fun FlowContent.renderUiCard() {
        noui.card {
            noui.content {
                ui.header H2 { +"The UI" }

                ui.divider()

                MarkdownDisplay(
                    """
                        Looking good thanks to:

                        - **[Semantic UI](https://semantic-ui.com)** / **[Fomantic UI](https://fomantic-ui.com)** — the UI framework behind the interface
                        - **[CommonMark](https://github.com/commonmark/commonmark-java)** / **[Marked](https://marked.js.org)** — markdown rendering (JVM / JS)
                        - **John M. Carroll's training wheels and minimalist instruction**: the first-visit trail (the Gui-dance) lights
                          one thing to try at a time and keeps the rest quiet, after *"Training Wheels in a User Interface"* (John M.
                          Carroll and Caroline Carrithers, Communications of the ACM, 1984) and *The Nurnberg Funnel: Designing
                          Minimalist Instruction for Practical Computer Skill* (John M. Carroll, MIT Press, 1990)
                    """.trimIndent()
                )
            }
        }
    }

    private fun FlowContent.renderDspAlgorithmsCard() {
        noui.card {
            noui.content {
                ui.header H2 { +"DSP Algorithms & Techniques" }

                ui.divider()

                MarkdownDisplay(
                    """
                        Beyond the filters, Klang's voices and effects build on well-known DSP algorithms:

                        - **Freeverb** by Jezar at Dreampoint — the reverb (Schroeder/Moorer architecture, public domain)
                        - **Karplus–Strong** (Kevin Karplus & Alexander Strong) — plucked-string synthesis
                        - **PolyBLEP** (Välimäki et al.) — band-limited oscillator anti-aliasing
                        - **Super-saw** after Adam Szabo's *"How to Emulate the Super Saw"* (Roland JP-8000)
                        - **Chebyshev-polynomial waveshaping** — harmonic distortion
                        - **Padé `tanh` approximation** `x(27+x²)/(27+9x²)` — the public-domain "27/9" fast-tanh
                        from the musicdsp.org / KVR community, used in Klang's soft-clipping (`ShapingFuncs.fastTanh`)
                        - **Minimax polynomial sine** — the oscillators' `fastSin` (`fast_math.kt`): an odd polynomial on
                        the folded half period, the classic method of Cecil Hastings Jr., *Approximations for Digital
                        Computers* (Princeton, 1955); the degree-11 coefficients were fitted for Klang, no third-party code
                        - **Table-and-polynomial `2^x` and `e^x`** — `fastExp2` and `fastExp` (`fast_math.kt`), the pitch
                        paths' ratio, the envelopes' exponential curve and the compressor's gain: the integer octave from
                        a table of powers of two, the fraction by a minimax polynomial, `e^x` as `2^(x·log2 e)`; the range
                        reduction of William J. Cody and William Waite, *Software Manual for the Elementary Functions*
                        (Prentice-Hall, 1980); the coefficients were fitted for Klang, no third-party code
                        - **Perlin noise** (Ken Perlin) — organic drift and noise textures
                        - **Berlin noise** (James Coyne and Jade Rowland, from Strudel's `berlin`): Perlin's drift with
                        straight segments. Klang joins hashed random values with straight lines, its hash the 32-bit
                        finalizer of Austin Appleby's MurmurHash3 (public domain); sprudel's `berlin` signal, the Ignitor's
                        `berlin` and the Katalyst's `Kat.berlin`
                        - **Fractal Brownian motion**, after Benoit B. Mandelbrot and John W. Van Ness's fractional
                        Brownian motion (SIAM Review, 1968): the noises' `octaves` and `persistence`, the same noise
                        layered at doubling speed and falling level
                        - **Euclidean rhythms** via the Bjorklund algorithm — rhythm generation
                        - **Lookahead limiting** after **[Geraint Luff](https://signalsmith-audio.co.uk/writing/2022/limiter/)**
                        (Signalsmith Audio) — the master limiter's anticipating gain path: a moving minimum of the
                        required gain, smoothed, applied to a delayed signal. Klang's implementation is its own; the
                        construction, and the insight that the smoothing has to fit *inside* the lookahead budget,
                        come from that write-up
                        - **Feed-forward dynamics topology** — the compressor's dB-domain peak detector and soft-knee
                        parabolic gain curve follow the standard design described across the DSP literature
                        (Giannoulis, Massberg & Reiss, *"Digital Dynamic Range Compressor Design"*, JAES 2012)
                        - **The missing fundamental**: the bass trick of Kokon and Der Schmetterling, a sine sub plus
                        partials at 2f to 8f, which a small speaker can play and the ear folds back into the fundamental it
                        cannot reproduce. The psychoacoustics is Jan F. Schouten's residue pitch (*"The residue, a new
                        component in subjective sound analysis"*, 1940); the production precedent is psychoacoustic bass
                        enhancement. Klang builds it additively, from its own sine partials
                        - **Loudness, ITU-R BS.1770 and EBU R 128**: the LUFS measure behind the songs' house level
                        (-14 LUFS) and the loudness-matched listening pairs that judge an engine change by ear
                        - **Parallel and multiband compression**: long-standing studio techniques, a compressed copy blended
                        under the dry signal and a compressor per frequency band; together the writing reference's parallel
                        multiband compression recipe, built in Klang from `bands` and `blend`
                    """.trimIndent()
                )
            }
        }
    }

    private fun FlowContent.renderHardwareCard() {
        noui.card {
            noui.content {
                ui.header H2 { +"Hardware Inspirations" }

                ui.divider()

                MarkdownDisplay(
                    """
                        The machines whose sounds Klang chases:

                        - **Roland JP-8000** (1997) — the synthesizer that introduced the **Super Saw**:
                        seven sawtooth oscillators — one centre, six detuned — in a single voice. The sound
                        that defined trance, and the reason a `supersaw` lives in every live-coding
                        environment today. Klang's super-oscillators chase that sound — by way of Adam
                        Szabo's analysis (see *DSP Algorithms & Techniques*) — while the implementation
                        is its own.
                        - **Roland TR-808 & TR-909** — the drum machines whose voices echo through the
                        bundled drum-machine sample banks (see *Samples & Soundfonts*), e.g. `bank("RolandTR808")`.
                    """.trimIndent()
                )
            }
        }
    }

    private fun FlowContent.renderSamplesCard() {
        noui.card {
            noui.content {
                ui.header H2 { +"Samples & Soundfonts" }

                ui.divider()

                MarkdownDisplay(
                    """
                        Klang ships with a default catalogue of openly available samples and soundfonts.
                        Huge thanks to everyone who created, curated, and hosts them:

                        - **[TidalCycles Dirt-Samples](https://github.com/tidalcycles/Dirt-Samples)** — the classic live-coding sample collection
                        - **[uzu-drumkit](https://github.com/tidalcycles/uzu-drumkit)** — the default drum kit,
                        with most samples contributed by [Switch Angel](https://www.youtube.com/@Switch-Angel)
                        - **Tidal Drum Machines** — emulation samples of classic drum machines
                        - **[Versilian Community Sample Library (VCSL)](https://github.com/sgossner/VCSL)** by Versilian Studios — CC0 acoustic-instrument samples
                        - **[Salamander Grand Piano](https://archive.org/details/SalamanderGrandPianoV3)** by Alexander Holm — the bundled piano samples ([CC BY 3.0](https://creativecommons.org/licenses/by/3.0/))
                        - **Mridangam samples** — © [Arthur Carabott](https://www.arthurcarabott.com/konnakkol/), performed by Harishankar V Menon ([CC BY-SA](https://creativecommons.org/licenses/by-sa/4.0/))
                        - **[dough-samples](https://github.com/felixroos/dough-samples)** by Felix Roos — hosting and curation of the bundled sample sets (Dirt-Samples, VCSL, mridangam, piano, drum machines)
                        - **[todepond/samples](https://github.com/todepond/samples)** by Lu Wilson — drum-machine sample aliases
                        - **[WebAudioFont](https://github.com/surikov/webaudiofont)** by Sergey Surikov — source of the bundled General MIDI soundfont
                    """.trimIndent()
                )
            }
        }
    }

    private fun FlowContent.renderGraphicsCard() {
        noui.card {
            noui.content {
                ui.header H2 { +"Graphics & Rendering" }

                ui.divider()

                MarkdownDisplay(
                    """
                        Visuals and editor effects are powered by these libraries (integrated via Kraft addons):

                        - **[PixiJS](https://pixijs.com)** — WebGL-accelerated rendering of live playback highlights in the editor
                        - **[Three.js](https://threejs.org)** — 3D graphics for the animated Motor background and visualizations
                        - **[OKLab](https://bottosson.github.io/posts/oklab/)** by Björn Ottosson: the perceptual colour space
                        a look's spectrum steps blend in, so Copper's blue fades into its copper through a warm grey, not a
                        green one
                    """.trimIndent()
                )
            }
        }
    }

    private fun FlowContent.renderKraftUltraCard() {
        noui.card {
            noui.content {
                ui.header H2 { +"Kraft & Ultra" }

                ui.divider()

                MarkdownDisplay(
                    """
                        Klang's frontend and shared building blocks are built on **Kraft** and **Ultra** —
                        PeekAndPoke's own open-source Kotlin frameworks:

                        - **[Kraft](https://github.com/PeekAndPoke)** — the reactive UI framework (VDom, components, forms,
                        routing) the entire interface is built with
                        - **[Ultra](https://github.com/PeekAndPoke)** — the foundational Kotlin libraries (html DSL, streams,
                        common utilities, dependency injection, and more)
                    """.trimIndent()
                )
            }
        }
    }

    private fun FlowContent.renderRuntimeCard() {
        noui.card {
            noui.content {
                ui.header H2 { +"The Runtime" }

                ui.divider()

                MarkdownDisplay(
                    """
                        Bridging worlds:

                        - **[GraalVM](https://www.graalvm.org)** — polyglot runtime used in early development 
                          to feed the audio engine with strudel events produced by the original JavaScript implementation
                    """.trimIndent()
                )
            }
        }
    }

    private fun FlowContent.renderTestingCard() {
        noui.card {
            noui.content {
                ui.header H2 { +"Testing" }

                ui.divider()

                MarkdownDisplay(
                    """
                        Keeping things working:

                        - **[Kotest](https://kotest.io)** — Kotlin testing framework
                    """.trimIndent()
                )
            }
        }
    }

    private fun FlowContent.renderAiCard() {
        noui.card {
            noui.content {
                ui.header H2 { +"AI Assistance" }

                ui.divider()

                MarkdownDisplay(
                    """
                        A significant amount of Klang's code was written with the help of AI.
                        **[Claude](https://claude.ai)** by **[Anthropic](https://anthropic.com)** helped achieve
                        a lot of things in a very short time, making it possible for a small project to move fast
                        and punch above its weight. Working with Claude has been an overall delightful experience.
                    """.trimIndent()
                )
            }
        }
    }
}
